import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, FileCheck2, Stethoscope, Snowflake, Languages, Landmark, Route } from "lucide-react";
import { useReveal } from "@/components/marketing/Sections";

const PARTNERS = [
  { name: "Pfizer", src: "/partners/pfizer.svg" },
  { name: "Novartis", src: "/partners/novartis.svg" },
  { name: "Roche", src: "/partners/roche.svg" },
  { name: "Sanofi", src: "/partners/sanofi.svg" },
  { name: "AstraZeneca", src: "/partners/astrazeneca.svg" },
  { name: "GSK", src: "/partners/gsk.svg" },
  { name: "Merck", src: "/partners/merck.svg" },
  { name: "Bayer", src: "/partners/bayer.svg" },
  { name: "Johnson & Johnson", src: "/partners/jnj.svg" },
  { name: "AbbVie", src: "/partners/abbvie.svg" },
];

export function Partners() {
  const reveal = useReveal();
  return (
    <section id="partners" className="scroll-mt-20 border-y border-[#D7E3F2] bg-white py-12 lg:py-14" data-testid="partners-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.p {...reveal(0)} className="text-center text-xs font-semibold uppercase tracking-[0.2em] text-[#64748B]">
          Sourcing products from the world's leading manufacturers
        </motion.p>
        <div className="mt-8 grid grid-cols-2 items-center gap-x-6 gap-y-8 sm:grid-cols-3 lg:grid-cols-5">
          {PARTNERS.map((p, i) => (
            <motion.div key={p.name} {...reveal(0.03 * i)} className="flex h-14 items-center justify-center" data-testid={`partner-logo-${i}`}>
              <img
                src={p.src}
                alt={`${p.name} logo`}
                title={p.name}
                className="max-h-10 w-auto max-w-[140px] object-contain opacity-60 grayscale transition-all duration-300 hover:opacity-100 hover:grayscale-0"
                loading="lazy"
              />
            </motion.div>
          ))}
        </div>
        <p className="mt-8 text-center text-[11px] text-[#94A3B8]">
          All trademarks are the property of their respective owners. Logos indicate manufacturers whose products we source and supply.
        </p>
      </div>
    </section>
  );
}

const COUNTRIES = [
  { c: "Brazil", d: "Regular supply of originator products to hospitals and importers across Brazil.", code: "BR" },
  { c: "Mexico", d: "Nationwide cold-chain delivery to hospitals, clinics and licensed distributors.", code: "MX" },
  { c: "Panama", d: "A key gateway serving Central America and the Caribbean.", code: "PA" },
  { c: "Peru", d: "Temperature-monitored shipments to Lima and regional destinations.", code: "PE" },
];

export function Latam() {
  const reveal = useReveal();
  return (
    <section id="latam" className="scroll-mt-20 bg-[#F8FAFC] py-20 lg:py-28" data-testid="latam-section">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-12 px-4 sm:px-6 lg:grid-cols-12 lg:gap-16 lg:px-8">
        <div className="lg:col-span-5">
          <motion.div {...reveal(0)}>
            <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-[#377DD3]">
              <span className="h-px w-6 bg-[#F5C64D]" /> Latin America expertise
            </span>
          </motion.div>
          <motion.h2 {...reveal(0.06)} className="font-heading mt-4 text-3xl font-bold tracking-tight text-[#0D1B2A] sm:text-4xl">
            Deep roots across LATAM, from Mexico to Brazil.
          </motion.h2>
          <motion.p {...reveal(0.1)} className="mt-5 text-base leading-relaxed text-[#64748B] sm:text-lg">
            Latin America is our specialist corridor. We ship critical medicines to hospitals,
            clinics and distributors across the continent every week — sourcing from originator
            manufacturers and delivering to the final destination with full cold-chain integrity.
          </motion.p>
          <div className="mt-8 space-y-4">
            {[
              { icon: Landmark, t: "Established LATAM client base", d: "Long-standing relationships with hospitals, importers and distributors in the region." },
              { icon: Languages, t: "Spanish & Portuguese desk", d: "Bilingual trade team for quotes, orders and shipment updates in your language." },
              { icon: Route, t: "Proven air corridors", d: "Dubai → Mexico City / Panama City / Lima / São Paulo lanes with validated transit times." },
            ].map((p, i) => (
              <motion.div key={p.t} {...reveal(0.12 + i * 0.06)} className="flex gap-4">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-[#377DD3] to-[#245FAF] text-white shadow-md"><p.icon size={20} /></div>
                <div>
                  <h3 className="font-semibold text-[#0D1B2A]">{p.t}</h3>
                  <p className="mt-1 text-sm text-[#64748B]">{p.d}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
        <div className="grid gap-5 sm:grid-cols-2 lg:col-span-7">
          {COUNTRIES.map((k, i) => (
            <motion.div key={k.c} {...reveal(0.08 + i * 0.06)} className="group relative overflow-hidden rounded-3xl border border-[#D7E3F2] bg-white p-7 transition-all hover:-translate-y-1 hover:border-[#377DD3]/40 hover:shadow-[0_28px_50px_-24px_rgba(36,95,175,0.4)]" data-testid={`latam-country-${k.code}`}>
              <div className="absolute right-5 top-5 font-heading text-5xl font-extrabold text-[#EAF2FB] transition-colors group-hover:text-[#F5C64D]/30" aria-hidden="true">{k.code}</div>
              <div className="relative flex h-10 w-10 items-center justify-center rounded-full bg-[#F5C64D]/20 text-[#245FAF]"><span className="h-2.5 w-2.5 rounded-full bg-[#F5C64D] ring-4 ring-[#F5C64D]/30" /></div>
              <h3 className="relative mt-5 text-xl font-semibold text-[#0D1B2A]">{k.c}</h3>
              <p className="relative mt-2 text-sm leading-relaxed text-[#64748B]">{k.d}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

const NP_STEPS = [
  { icon: Stethoscope, t: "Request & quote", d: "Your hospital, pharmacy or importer sends us the product, strength and quantity needed for a specific patient. We quote within one business day." },
  { icon: FileCheck2, t: "Sourced from the originator", d: "We source the genuine product from the manufacturer or its authorised channel, with batch and expiry details confirmed before dispatch." },
  { icon: Snowflake, t: "Shipped to final destination", d: "Packed to GDP standards and flown temperature-monitored to your nominated address, with tracking in the portal." },
];

export function NamedPatient() {
  const reveal = useReveal();
  return (
    <section id="named-patient" className="scroll-mt-20 relative overflow-hidden bg-white py-20 lg:py-28" data-testid="named-patient-section">
      <div className="pointer-events-none absolute -left-32 top-1/3 h-96 w-96 rounded-full bg-[#F5C64D]/10 blur-3xl" aria-hidden="true" />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-5">
            <motion.div {...reveal(0)}>
              <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-[#377DD3]">
                <span className="h-px w-6 bg-[#F5C64D]" /> Our specialty
              </span>
            </motion.div>
            <motion.h2 {...reveal(0.06)} className="font-heading mt-4 text-3xl font-bold tracking-tight text-[#0D1B2A] sm:text-4xl">
              Named Patient Supply to many countries.
            </motion.h2>
            <motion.p {...reveal(0.1)} className="mt-5 text-base leading-relaxed text-[#64748B] sm:text-lg">
              When a patient needs a medicine that is unlicensed, unavailable or in shortage in their
              country, we make access possible. We source originator products — including orphan,
              oncology and rare-disease therapies — and ship them cold-chain to the final destination.
              Import permits and local regulatory filings remain with the importing party.
            </motion.p>
            <motion.ul {...reveal(0.14)} className="mt-6 flex flex-wrap gap-2.5">
              {["Unlicensed medicines", "Orphan & rare disease", "Oncology & biologics", "Shortage mitigation", "Single-patient quantities", "Cold-chain to destination"].map((t) => (
                <li key={t} className="rounded-full border border-[#D7E3F2] bg-[#F8FAFC] px-3.5 py-1.5 text-xs font-medium text-[#0D1B2A]">{t}</li>
              ))}
            </motion.ul>
            <motion.div {...reveal(0.18)} className="mt-8">
              <a href="#contact" data-testid="named-patient-cta" className="group inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#377DD3] to-[#245FAF] px-6 py-3 text-sm font-semibold text-white shadow-[0_14px_34px_-12px_rgba(55,125,211,0.9)] ring-1 ring-inset ring-[#F5C64D]/50 transition-all hover:scale-[1.02]">
                Request a named patient quote <ArrowRight size={16} className="transition-transform group-hover:translate-x-0.5" />
              </a>
            </motion.div>
          </div>
          <div className="lg:col-span-7">
            <ol className="relative space-y-6 border-l-2 border-dashed border-[#D7E3F2] pl-8 lg:mt-6">
              {NP_STEPS.map((s, i) => (
                <motion.li key={s.t} {...reveal(0.1 + i * 0.08)} className="relative" data-testid={`named-patient-step-${i}`}>
                  <span className="absolute -left-[45px] flex h-7 w-7 items-center justify-center rounded-full bg-[#F5C64D] text-xs font-bold text-[#0D1B2A] ring-4 ring-white">{i + 1}</span>
                  <div className="rounded-3xl border border-[#D7E3F2] bg-[#F8FAFC] p-6">
                    <div className="flex items-center gap-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-[#377DD3] to-[#245FAF] text-white"><s.icon size={18} /></div>
                      <h3 className="text-lg font-semibold text-[#0D1B2A]">{s.t}</h3>
                    </div>
                    <p className="mt-3 text-sm leading-relaxed text-[#64748B]">{s.d}</p>
                  </div>
                </motion.li>
              ))}
            </ol>
            <motion.div {...reveal(0.3)} className="mt-6 flex flex-col items-start justify-between gap-4 rounded-3xl bg-[#0D1B2A] p-6 text-white sm:flex-row sm:items-center">
              <div>
                <div className="text-sm font-semibold">Serving 40+ countries across LATAM, Africa, the Gulf and Asia</div>
                <div className="mt-1 text-xs text-white/60">Typical lead time 7–14 days from confirmed order to delivery.</div>
              </div>
              <Link to="/login" className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#F5C64D] hover:underline" data-testid="named-patient-portal-cta">
                Track in portal <ArrowRight size={15} />
              </Link>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
